import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AgentTraceListPane } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_list_pane';
import {
    agentTraceSearchHasExplicitSavedViewState,
    AgentTraceViewPills,
} from '@apps/web/routes/_app/admin/agent-traces/agent_trace_view_pills';
import { AgentTraceViewTabs } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_view_tabs';
import {
    type AgentTracesSearch,
    agentTracesSearchSchema,
    filterChipsToSearchParams,
    searchToFilterChips,
    type TraceFilterChip,
} from '@apps/web/routes/_app/admin/agent-traces/search_params';
import { useAdminAgentTraceSavedViews } from '@apps/web/swr_data_fetching/admin_agent_traces';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type AgentTraceSavedViewRead } from '@libraries/typescript/api_client';

const EMPTY_SAVED_VIEWS: ReadonlyArray<AgentTraceSavedViewRead> = [];
const STORAGE_KEY_DEFAULT_VIEW = 'admin-agent-traces-default-view';

export const Route = createFileRoute('/_app/admin/agent-traces/')({
    component: AgentTracesPage,
    head: () => ({ meta: [{ title: 'Agent Traces' }] }),
    validateSearch: agentTracesSearchSchema,
});

function AgentTracesPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { data: savedViewsResponse, mutate: mutateSavedViews } =
        useAdminAgentTraceSavedViews();
    const { data: currentUser } = useUser({ reason: 'admin-agent-traces' });
    const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(
        null,
    );
    const [defaultViewId, setDefaultViewId] = useState<string | null>(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY_DEFAULT_VIEW);
        } catch {
            return null;
        }
    });
    const initializedSavedViewRef = useRef(false);

    const updateSearch = useCallback(
        (
            updates: Partial<AgentTracesSearch>,
            options?: { readonly replace?: boolean },
        ) => {
            navigate({
                search: (current) => ({ ...current, ...updates }),
                replace: options?.replace,
            });
        },
        [navigate],
    );

    const handleFilterChipsChange = useCallback(
        (nextChips: Array<TraceFilterChip>) => {
            updateSearch({
                ...filterChipsToSearchParams(nextChips),
                page: 1,
            });
        },
        [updateSearch],
    );

    const handleApplySavedView = useCallback(
        (savedView: AgentTraceSavedViewRead) => {
            setActiveSavedViewId(savedView.id);
            const chips = savedView.payload.filters.map((filter) => ({
                dimension: filter.dimension,
                operator: filter.operator,
                values: [...filter.values],
            }));
            void navigate({
                replace: true,
                search: (current) => ({
                    ...current,
                    ...filterChipsToSearchParams(chips),
                    view: undefined,
                    created_after: undefined,
                    created_before: undefined,
                    window:
                        savedView.payload.window_days === null
                            ? undefined
                            : savedView.payload.window_days,
                    sort_by: savedView.payload.sort_by,
                    sort_judge_label:
                        savedView.payload.sort_by === 'judge_label'
                            ? (savedView.payload.sort_judge_label ?? undefined)
                            : undefined,
                    sort_order: savedView.payload.sort_order,
                    page: 1,
                }),
            });
        },
        [navigate],
    );

    const handleClearSavedView = useCallback(() => {
        setActiveSavedViewId(null);
        void navigate({
            replace: true,
            search: (current) => ({
                ...current,
                ...filterChipsToSearchParams([]),
                view: undefined,
                created_after: undefined,
                created_before: undefined,
                window: undefined,
                sort_by: undefined,
                sort_judge_label: undefined,
                sort_order: undefined,
                page: 1,
            }),
        });
    }, [navigate]);

    const handleDefaultViewChange = useCallback((viewId: string | null) => {
        setDefaultViewId(viewId);
        try {
            if (viewId === null) {
                window.localStorage.removeItem(STORAGE_KEY_DEFAULT_VIEW);
            } else {
                window.localStorage.setItem(STORAGE_KEY_DEFAULT_VIEW, viewId);
            }
        } catch {
            // Keep the in-memory preference when browser storage is unavailable.
        }
    }, []);

    useEffect(() => {
        if (search.view === undefined) {
            return;
        }

        setActiveSavedViewId(null);
        if (search.view === 'all') {
            void navigate({
                replace: true,
                search: (current) => ({ ...current, view: undefined }),
            });
            return;
        }

        const humanReviewStatus =
            search.view === 'needs_review' ? 'not_reviewed' : 'reviewed';
        const nextChips: Array<TraceFilterChip> = [
            ...searchToFilterChips(search).filter(
                (chip) => chip.dimension !== 'human_review_status',
            ),
            {
                dimension: 'human_review_status',
                operator: 'is',
                values: [humanReviewStatus],
            },
        ];
        void navigate({
            replace: true,
            search: (current) => ({
                ...current,
                ...filterChipsToSearchParams(nextChips),
                view: undefined,
            }),
        });
    }, [navigate, search]);

    useEffect(() => {
        if (initializedSavedViewRef.current) {
            return;
        }
        if (agentTraceSearchHasExplicitSavedViewState(search)) {
            initializedSavedViewRef.current = true;
            return;
        }
        if (savedViewsResponse === undefined) {
            return;
        }

        initializedSavedViewRef.current = true;
        const personalDefaultView =
            defaultViewId === null
                ? undefined
                : savedViewsResponse.views.find(
                      (candidate) => candidate.id === defaultViewId,
                  );
        const defaultView =
            personalDefaultView ??
            savedViewsResponse.views.find(
                (candidate) =>
                    candidate.is_team_default && candidate.scope === 'shared',
            );
        if (defaultView !== undefined) {
            handleApplySavedView(defaultView);
        }
    }, [defaultViewId, handleApplySavedView, savedViewsResponse, search]);

    return (
        <div className="flex flex-col gap-4 p-6">
            <AgentTraceViewTabs activeView="conversations" />
            <AgentTraceViewPills
                views={savedViewsResponse?.views ?? EMPTY_SAVED_VIEWS}
                search={search}
                activeViewId={activeSavedViewId}
                defaultViewId={defaultViewId}
                currentUserEmail={currentUser?.email ?? null}
                onApplyView={handleApplySavedView}
                onClearView={handleClearSavedView}
                onFilterChipsChange={handleFilterChipsChange}
                onDefaultViewChange={handleDefaultViewChange}
                onSavedViewsRefresh={mutateSavedViews}
            />
            <AgentTraceListPane search={search} onSearchChange={updateSearch} />
        </div>
    );
}
